export interface LeaderboardEntry {
  id: string;
  playerName: string;
  score: number;
  apm: number;
  survivalTime: number;
  peakAgents: number;
  rank: string;
  dr: number;
  vs: number;
  ce: number;
  ta: number;
  createdAt: string;
}

const SUPABASE_URL = 'https://ozrrqjcaymdqamviigwp.supabase.co';
const SUPABASE_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im96cnJxamNheW1kcWFtdmlpZ3dwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzUyMzE3MjMsImV4cCI6MjA5MDgwNzcyM30.8mSMeZ-c6lvbBEP3BoF5ktn_OSIxWgEzRhrGP582rRk';

const headers = {
  'Content-Type': 'application/json',
  'apikey': SUPABASE_KEY,
  'Authorization': `Bearer ${SUPABASE_KEY}`,
};

export class LeaderboardService {
  async submitScore(entry: Omit<LeaderboardEntry, 'id' | 'createdAt'>): Promise<void> {
    try {
      await fetch(`${SUPABASE_URL}/rest/v1/scores`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          player_name: entry.playerName,
          score: entry.score,
          apm: entry.apm,
          survival_time: entry.survivalTime,
          peak_agents: entry.peakAgents,
          rank: entry.rank,
          dr: entry.dr,
          vs: entry.vs,
          ce: entry.ce,
          ta: entry.ta,
        }),
      });
    } catch {
      // Supabase unavailable — score lost, no fallback
    }
  }

  async getTopScores(limit = 100): Promise<LeaderboardEntry[]> {
    try {
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/scores?order=score.desc&limit=${limit}&select=id,player_name,score,apm,survival_time,peak_agents,rank,dr,vs,ce,ta,created_at`,
        { headers }
      );
      if (!res.ok) return [];
      const data = await res.json();
      return data.map((row: any) => ({
        id: row.id,
        playerName: row.player_name,
        score: row.score,
        apm: row.apm,
        survivalTime: row.survival_time,
        peakAgents: row.peak_agents,
        rank: row.rank,
        dr: row.dr,
        vs: row.vs,
        ce: row.ce,
        ta: row.ta,
        createdAt: row.created_at,
      }));
    } catch {
      return [];
    }
  }

  async getPlayerRank(score: number): Promise<{ rank: number; percentile: number; total: number }> {
    try {
      // Count how many scores are higher
      const countRes = await fetch(
        `${SUPABASE_URL}/rest/v1/scores?score=gt.${score}&select=id`,
        { headers: { ...headers, 'Prefer': 'count=exact' } }
      );
      const rank = parseInt(countRes.headers.get('content-range')?.split('/')[1] || '0', 10) > 0
        ? parseInt(countRes.headers.get('content-range')?.split('/')[0]?.split('-')[1] || '0', 10) + 2
        : 1;

      // Get total count
      const totalRes = await fetch(
        `${SUPABASE_URL}/rest/v1/scores?select=id`,
        { headers: { ...headers, 'Prefer': 'count=exact' } }
      );
      const total = parseInt(totalRes.headers.get('content-range')?.split('/')[1] || '0', 10);

      const percentile = total > 0 ? Math.round((1 - rank / total) * 100) : 100;
      return { rank, percentile, total };
    } catch {
      return { rank: 1, percentile: 100, total: 0 };
    }
  }
}
